/**
 * Profile Management API Service
 *
 * Handles user profile, avatar, password, and API key management
 */

import { supabase } from '../lib/supabase/client';
import { LogService } from './logService';

// Only log in development mode
const isDev = import.meta.env.DEV;
const devLog = (message: string, ...args: unknown[]) => {
  if (isDev) console.log(message, ...args);
};

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Storage key used by Supabase client (must match secureTokenStorage prefix + key)
const STORAGE_PREFIX = 'wealthhub-secure-';
const STORAGE_KEY = 'wealthhub-auth';

/**
 * Fast session retrieval directly from localStorage
 * This avoids waiting for Supabase client initialization/network calls
 */
function getSessionFromStorage(): { access_token: string; refresh_token: string } | null {
  try {
    // Read from the correct prefixed key (matching secureTokenStorage)
    const stored = localStorage.getItem(STORAGE_PREFIX + STORAGE_KEY);
    if (!stored) {
      return null;
    }

    // Decode base64 (matching secureTokenStorage encoding)
    let decoded: string;
    try {
      decoded = atob(stored);
      // Verify it's valid JSON after decoding
      JSON.parse(decoded);
    } catch {
      // Not valid base64 or the decoded result isn't JSON
      // Try treating it as raw JSON
      try {
        JSON.parse(stored);
        decoded = stored;
      } catch {
        devLog('[ProfileAPI] Stored value is neither base64 nor valid JSON');
        return null;
      }
    }

    const parsed = JSON.parse(decoded);
    // Handle potential wrapped session format (e.g., { currentSession: {...} })
    const session = parsed?.currentSession || parsed;
    const accessToken = session?.access_token;
    const refreshToken = session?.refresh_token;

    if (!accessToken) {
      return null;
    }

    // Check if token is expired (JWT has 3 parts separated by dots)
    const parts = accessToken.split('.');
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1]));
        const exp = payload.exp;
        if (exp && Date.now() >= exp * 1000) {
          devLog('[ProfileAPI] Token expired, will need refresh');
          // Token expired, but we can still try with refresh token
          // Return it anyway - Supabase will handle refresh
        }
      } catch (e) {
        // Can't parse token, continue anyway
      }
    }

    return { access_token: accessToken, refresh_token: refreshToken };
  } catch (err) {
    devLog('[ProfileAPI] Error reading session from storage:', err);
    return null;
  }
}

// Helper to get auth header (with fast localStorage fallback)
async function getAuthHeader(): Promise<Record<string, string>> {
  // Fast path: Try to get session directly from localStorage first
  const storedSession = getSessionFromStorage();
  if (storedSession?.access_token) {
    devLog('[ProfileAPI] Using session from localStorage (fast path)');
    return {
      'Authorization': `Bearer ${storedSession.access_token}`,
      'Content-Type': 'application/json',
    };
  }

  // Slow path: Fall back to Supabase client with timeout
  try {
    devLog('[ProfileAPI] Falling back to Supabase client for session');
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Auth session timeout')), 10000); // 10 seconds for slow networks
    });

    const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  } catch (err: any) {
    devLog('[ProfileAPI] getAuthHeader error:', err.message);
    throw new Error('Not authenticated');
  }
}

// Helper to parse response with better error messages
async function parseResponse<T>(response: Response, context: string): Promise<T> {
  const text = await response.text();

  try {
    const json = JSON.parse(text);

    if (!response.ok) {
      const errorMsg = json.error || json.message || `${context} failed`;
      devLog(`[ProfileAPI] ${context} error:`, json);
      throw new Error(errorMsg);
    }

    return json;
  } catch (parseError) {
    // JSON parsing failed - server returned HTML or plain text
    devLog(`[ProfileAPI] ${context} - Server returned non-JSON response:`);
    devLog(`[ProfileAPI] Status: ${response.status} ${response.statusText}`);
    devLog(`[ProfileAPI] Response (first 500 chars):`, text.substring(0, 500));

    if (text.includes('A server error has occurred')) {
      throw new Error(`${context}: Vercel server error - check function logs in Vercel dashboard`);
    }
    if (text.includes('FUNCTION_INVOCATION_FAILED')) {
      throw new Error(`${context}: Function crashed - check Vercel logs for stack trace`);
    }
    if (text.includes('<!DOCTYPE') || text.includes('<html')) {
      throw new Error(`${context}: Server returned HTML instead of JSON (status ${response.status})`);
    }

    throw new Error(`${context}: Invalid response - ${text.substring(0, 100)}`);
  }
}

// ===== PROFILE =====

export interface UserProfileData {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  secondary_email: string | null;
  bio: string | null;
  subscription_tier: string;
  settings: Record<string, any>;
  email?: string;
  email_verified?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get current user profile (with timeout)
 */
export async function getProfile(): Promise<UserProfileData> {
  const headers = await getAuthHeader();

  // Add timeout to avoid blocking
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const result = await parseResponse<{ data: UserProfileData }>(response, 'Get profile');
    return result.data;
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('Profile request timed out');
    }
    throw err;
  }
}

/**
 * Update user profile
 */
export async function updateProfile(data: {
  username?: string;
  display_name?: string;
  secondary_email?: string;
  bio?: string;
  avatar_url?: string;
}): Promise<UserProfileData> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  const result = await parseResponse<{ data: UserProfileData }>(response, 'Update profile');
  return result.data;
}

/**
 * Change password
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile/password`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  await parseResponse(response, 'Change password');
}

/**
 * Upload avatar
 */
export async function uploadAvatar(imageFile: File): Promise<string> {
  const headers = await getAuthHeader();

  // Convert file to base64
  const base64 = await fileToBase64(imageFile);

  const response = await fetch(`${API_BASE}/profile/avatar`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      image: base64,
      contentType: imageFile.type,
    }),
  });

  const result = await parseResponse<{ data: { avatar_url: string } }>(response, 'Upload avatar');
  return result.data.avatar_url;
}

/**
 * Delete avatar
 */
export async function deleteAvatar(): Promise<void> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile/avatar`, {
    method: 'DELETE',
    headers,
  });

  await parseResponse(response, 'Delete avatar');
}

// Helper to convert file to base64
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

// ===== API KEYS =====

export interface ApiKeyData {
  id: string;
  key_prefix: string;
  key?: string; // Only returned on creation
  name: string;
  permissions: { read: boolean; write: boolean };
  rate_limit_per_minute: number;
  is_active: boolean;
  last_used_at: string | null;
  usage_count: number;
  expires_at: string | null;
  created_at: string;
}

/**
 * List user's API keys (with timeout)
 */
export async function listApiKeys(): Promise<ApiKeyData[]> {
  const headers = await getAuthHeader();

  // Add timeout to avoid blocking
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${API_BASE}/profile/api-keys`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const result = await parseResponse<{ data: ApiKeyData[] }>(response, 'List API keys');
    return result.data;
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error('API keys request timed out');
    }
    throw err;
  }
}

/**
 * Create new API key
 */
export async function createApiKey(options?: {
  name?: string;
  permissions?: { read: boolean; write: boolean };
  expires_in_days?: number;
}): Promise<ApiKeyData> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile/api-keys`, {
    method: 'POST',
    headers,
    body: JSON.stringify(options || {}),
  });

  const result = await parseResponse<{ data: ApiKeyData }>(response, 'Create API key');
  return result.data;
}

/**
 * Revoke API key
 */
export async function revokeApiKey(keyId: string): Promise<void> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile/api-keys`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ key_id: keyId }),
  });

  await parseResponse(response, 'Revoke API key');
}

/**
 * Update API key
 */
export async function updateApiKey(keyId: string, data: {
  name?: string;
  is_active?: boolean;
}): Promise<ApiKeyData> {
  const headers = await getAuthHeader();

  const response = await fetch(`${API_BASE}/profile/api-keys`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ key_id: keyId, ...data }),
  });

  const result = await parseResponse<{ data: ApiKeyData }>(response, 'Update API key');
  return result.data;
}
